import React, { useState } from 'react';
import {
  AgentDecision,
  DecisionAlternative,
  AIAgent,
  DecisionType,
  RiskLevel
} from '../../../types/aiAgent';
import { tone, toneForPriority } from "components/ui/tone";

interface AIAgentDecisionExplainerProps {
  decision: AgentDecision;
  agent: AIAgent;
  onApprove?: (decisionId: string, feedback?: string) => void;
  onReject?: (decisionId: string, reason: string) => void;
  onRequestMoreInfo?: (decisionId: string, questions: string[]) => void;
}

export const AIAgentDecisionExplainer: React.FC<AIAgentDecisionExplainerProps> = ({
  decision,
  agent,
  onApprove,
  onReject,
  onRequestMoreInfo
}) => {
  const [activeTab, setActiveTab] = useState<'summary' | 'reasoning' | 'data' | 'alternatives' | 'impact'>('summary');
  const [showApprovalDialog, setShowApprovalDialog] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [questions, setQuestions] = useState<string[]>(['']);

  const getDecisionTypeIcon = (type: DecisionType) => {
    const icons = {
      test_creation: '🧪',
      variant_generation: '🔄',
      winner_implementation: '🏆',
      campaign_optimization: '🚀',
      audience_targeting: '🎯',
      statistical_analysis: '📊',
      content_modification: '✏️',
      resource_allocation: '💰'
    };
    return icons[type] || '🤖';
  };

  // Risk reads on the same ramp as priority and severity.
  const getRiskLevelColor = (risk: RiskLevel) => tone(toneForPriority(risk)).chip;

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 0.9) return 'text-green-600 dark:text-green-300 bg-green-100 dark:bg-green-900/30';
    if (confidence >= 0.7) return 'text-blue-600 dark:text-blue-300 bg-blue-100 dark:bg-blue-900/30';
    if (confidence >= 0.5) return 'text-yellow-600 dark:text-yellow-300 bg-yellow-100 dark:bg-yellow-900/30';
    return 'text-red-600 dark:text-red-300 bg-red-100 dark:bg-red-900/30';
  };

  const handleApprove = () => {
    onApprove?.(decision.id, feedback || undefined);
    setShowApprovalDialog(false);
    setFeedback('');
  };

  const handleReject = () => {
    if (rejectionReason.trim()) {
      onReject?.(decision.id, rejectionReason);
      setRejectionReason('');
    }
  };

  const handleRequestMoreInfo = () => {
    const validQuestions = questions.filter(q => q.trim());
    if (validQuestions.length > 0) {
      onRequestMoreInfo?.(decision.id, validQuestions);
      setQuestions(['']);
    }
  };

  const addQuestion = () => {
    setQuestions([...questions, '']);
  };

  const updateQuestion = (index: number, value: string) => {
    const updated = [...questions];
    updated[index] = value;
    setQuestions(updated);
  };

  const removeQuestion = (index: number) => {
    setQuestions(questions.filter((_, i) => i !== index));
  };

  return (
    <div className="bg-surface rounded-lg shadow-lg border border-line max-w-4xl mx-auto">
      {/* Header */}
      <div className="border-b border-line px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="text-2xl">{getDecisionTypeIcon(decision.type)}</div>
            <div>
              <h3 className="text-lg font-semibold text-content">
                Decision Explanation
              </h3>
              <p className="text-sm text-content-secondary">
                {decision.type.replace('_', ' ').toUpperCase()} by {agent.name}
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${getConfidenceColor(decision.confidence)}`}>
              {(decision.confidence * 100).toFixed(1)}% Confidence
            </span>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${getRiskLevelColor(decision.riskLevel)}`}>
              {decision.riskLevel.toUpperCase()} Risk
            </span>
          </div>
        </div>

        {/* Decision Status */}
        <div className="mt-4 flex items-center justify-between">
          <div className="text-sm text-content-secondary">
            <span>Decision made: {decision.timestamp.toLocaleString()}</span>
            {decision.approvedAt && (
              <span className="ml-4">
                Approved: {decision.approvedAt.toLocaleString()}
              </span>
            )}
          </div>
          
          <div className="flex items-center space-x-2">
            {decision.approvalStatus === 'pending' && (
              <>
                <button
                  onClick={() => setShowApprovalDialog(true)}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
                >
                  Approve
                </button>
                <button
                  onClick={() => setRejectionReason('Please provide a reason for rejection')}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"
                >
                  Reject
                </button>
                <button
                  onClick={() => setQuestions([''])}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                >
                  Request Info
                </button>
              </>
            )}
            {decision.approvalStatus === 'approved' && (
              <span className="px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 rounded-full text-sm font-medium">
                ✓ Approved
              </span>
            )}
            {decision.approvalStatus === 'rejected' && (
              <span className="px-3 py-1 bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 rounded-full text-sm font-medium">
                ✗ Rejected
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="border-b border-line">
        <nav className="flex space-x-8 px-6">
          {[
            { id: 'summary', label: 'Summary', icon: '📋' },
            { id: 'reasoning', label: 'Reasoning', icon: '🧠' },
            { id: 'data', label: 'Supporting Data', icon: '📊' },
            { id: 'alternatives', label: 'Alternatives', icon: '🔀' },
            { id: 'impact', label: 'Expected Impact', icon: '📈' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600 dark:text-blue-300'
                  : 'border-transparent text-content-muted hover:text-gray-700 dark:hover:text-gray-300 hover:border-line-strong'
              }`}
            >
              <span>{tab.icon}</span>
              <span>{tab.label}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="p-6">
        {activeTab === 'summary' && (
          <DecisionSummaryTab decision={decision} />
        )}

        {activeTab === 'reasoning' && (
          <ReasoningTab decision={decision} />
        )}

        {activeTab === 'data' && (
          <SupportingDataTab decision={decision} />
        )}

        {activeTab === 'alternatives' && (
          <AlternativesTab alternatives={decision.alternatives} />
        )}

        {activeTab === 'impact' && (
          <ExpectedImpactTab decision={decision} />
        )}
      </div>

      {/* Approval Dialog */}
      {showApprovalDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-surface rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b border-line">
              <h3 className="text-lg font-semibold text-content">Approve Decision</h3>
            </div>
            <div className="p-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Feedback (optional)
              </label>
              <textarea
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                placeholder="Provide feedback on this decision..."
                rows={3}
                className="w-full border border-line-strong rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="px-6 py-4 border-t border-line flex justify-end space-x-3">
              <button
                onClick={() => setShowApprovalDialog(false)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-line-strong rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700"
              >
                Cancel
              </button>
              <button
                onClick={handleApprove}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                Approve
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rejection Dialog */}
      {rejectionReason && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-surface rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b border-line">
              <h3 className="text-lg font-semibold text-content">Reject Decision</h3>
            </div>
            <div className="p-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Reason for rejection *
              </label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Explain why this decision should be rejected..."
                rows={3}
                className="w-full border border-line-strong rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>
            <div className="px-6 py-4 border-t border-line flex justify-end space-x-3">
              <button
                onClick={() => setRejectionReason('')}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-line-strong rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={!rejectionReason.trim()}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                Reject
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Request More Info Dialog */}
      {questions.length > 0 && questions[0] !== '' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-surface rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b border-line">
              <h3 className="text-lg font-semibold text-content">Request More Information</h3>
            </div>
            <div className="p-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Questions for the agent
              </label>
              <div className="space-y-2">
                {questions.map((question, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <input
                      type="text"
                      value={question}
                      onChange={(e) => updateQuestion(index, e.target.value)}
                      placeholder={`Question ${index + 1}...`}
                      className="flex-1 border border-line-strong rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {questions.length > 1 && (
                      <button
                        onClick={() => removeQuestion(index)}
                        className="text-red-600 dark:text-red-300 hover:text-red-800 dark:hover:text-red-300"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                ))}
              </div>
              <button
                onClick={addQuestion}
                className="mt-2 text-blue-600 dark:text-blue-300 hover:text-blue-800 dark:hover:text-blue-300 text-sm"
              >
                + Add another question
              </button>
            </div>
            <div className="px-6 py-4 border-t border-line flex justify-end space-x-3">
              <button
                onClick={() => setQuestions([''])}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-line-strong rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700"
              >
                Cancel
              </button>
              <button
                onClick={handleRequestMoreInfo}
                disabled={!questions.some(q => q.trim())}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                Send Questions
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Decision Summary Tab
const DecisionSummaryTab: React.FC<{ decision: AgentDecision }> = ({ decision }) => {
  return (
    <div className="space-y-6">
      <div>
        <h4 className="text-lg font-semibold text-content mb-3">Decision Summary</h4>
        <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-4">
          <p className="text-content">{decision.explanation.summary}</p>
        </div>
      </div>

      <div>
        <h5 className="font-medium text-content mb-2">Key Details</h5>
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-gray-50 dark:bg-dark-700 rounded-lg p-3">
            <div className="text-sm text-content-secondary">Decision Type</div>
            <div className="font-medium text-content capitalize">
              {decision.type.replace('_', ' ')}
            </div>
          </div>
          <div className="bg-gray-50 dark:bg-dark-700 rounded-lg p-3">
            <div className="text-sm text-content-secondary">Confidence Level</div>
            <div className="font-medium text-content">
              {(decision.confidence * 100).toFixed(1)}%
            </div>
          </div>
          <div className="bg-gray-50 dark:bg-dark-700 rounded-lg p-3">
            <div className="text-sm text-content-secondary">Risk Assessment</div>
            <div className="font-medium text-content capitalize">
              {decision.riskLevel}
            </div>
          </div>
          <div className="bg-gray-50 dark:bg-dark-700 rounded-lg p-3">
            <div className="text-sm text-content-secondary">Alternatives Considered</div>
            <div className="font-medium text-content">
              {decision.alternatives.length}
            </div>
          </div>
        </div>
      </div>

      {decision.explanation.assumptions.length > 0 && (
        <div>
          <h5 className="font-medium text-content mb-2">Key Assumptions</h5>
          <ul className="space-y-2">
            {decision.explanation.assumptions.map((assumption, index) => (
              <li key={index} className="flex items-start space-x-2">
                <span className="text-blue-600 dark:text-blue-300 mt-1">•</span>
                <span className="text-gray-700 dark:text-gray-300">{assumption}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

// Reasoning Tab
const ReasoningTab: React.FC<{ decision: AgentDecision }> = ({ decision }) => {
  return (
    <div className="space-y-6">
      <div>
        <h4 className="text-lg font-semibold text-content mb-3">Detailed Reasoning</h4>
        <div className="prose max-w-none">
          <div className="bg-gray-50 dark:bg-dark-700 rounded-lg p-4">
            <p className="text-content whitespace-pre-line">{decision.explanation.details}</p>
          </div>
        </div>
      </div>

      <div>
        <h5 className="font-medium text-content mb-3">Reasoning Process</h5>
        <div className="bg-surface border border-line rounded-lg p-4">
          <p className="text-gray-700 dark:text-gray-300 mb-4">{decision.reasoning}</p>
          
          {decision.context && (
            <div className="mt-4">
              <h6 className="font-medium text-content mb-2">Context Considered</h6>
              <div className="bg-gray-50 dark:bg-dark-700 rounded-lg p-3">
                <pre className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                  {JSON.stringify(decision.context, null, 2)}
                </pre>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Supporting Data Tab
const SupportingDataTab: React.FC<{ decision: AgentDecision }> = ({ decision }) => {
  return (
    <div className="space-y-6">
      <div>
        <h4 className="text-lg font-semibold text-content mb-3">Supporting Data Points</h4>
        {decision.explanation.dataPoints.length > 0 ? (
          <div className="space-y-4">
            {decision.explanation.dataPoints.map((dataPoint, index) => (
              <div key={index} className="border border-line rounded-lg p-4">
                <div className="font-medium text-content mb-2">
                  Data Point {index + 1}
                </div>
                <div className="bg-gray-50 dark:bg-dark-700 rounded-lg p-3">
                  <pre className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                    {typeof dataPoint === 'object' 
                      ? JSON.stringify(dataPoint, null, 2)
                      : String(dataPoint)
                    }
                  </pre>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <div className="text-content-subtle text-4xl mb-2">📊</div>
            <p className="text-content-secondary">No supporting data points available</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Alternatives Tab
const AlternativesTab: React.FC<{ alternatives: DecisionAlternative[] }> = ({ alternatives }) => {
  const getRiskColor = (risk: RiskLevel) => {
    return tone(toneForPriority(risk)).chip;
  };

  return (
    <div className="space-y-6">
      <div>
        <h4 className="text-lg font-semibold text-content mb-3">
          Alternative Options Considered ({alternatives.length})
        </h4>
        {alternatives.length > 0 ? (
          <div className="space-y-4">
            {alternatives.map((alternative, index) => (
              <div key={alternative.id} className="border border-line rounded-lg p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h5 className="font-medium text-content mb-1">
                      Alternative {index + 1}
                    </h5>
                    <p className="text-gray-700 dark:text-gray-300">{alternative.description}</p>
                  </div>
                  <div className="flex items-center space-x-2 ml-4">
                    <span className="text-sm font-medium text-content">
                      {(alternative.confidence * 100).toFixed(1)}%
                    </span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRiskColor(alternative.riskLevel)}`}>
                      {alternative.riskLevel}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h6 className="font-medium text-green-700 dark:text-green-300 mb-2">Pros</h6>
                    <ul className="space-y-1">
                      {alternative.pros.map((pro, i) => (
                        <li key={i} className="text-sm text-gray-700 dark:text-gray-300 flex items-start space-x-2">
                          <span className="text-green-600 dark:text-green-300 mt-1">+</span>
                          <span>{pro}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h6 className="font-medium text-red-700 dark:text-red-300 mb-2">Cons</h6>
                    <ul className="space-y-1">
                      {alternative.cons.map((con, i) => (
                        <li key={i} className="text-sm text-gray-700 dark:text-gray-300 flex items-start space-x-2">
                          <span className="text-red-600 dark:text-red-300 mt-1">-</span>
                          <span>{con}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {alternative.expectedOutcome && (
                  <div className="mt-3 bg-gray-50 dark:bg-dark-700 rounded-lg p-3">
                    <h6 className="font-medium text-content mb-1">Expected Outcome</h6>
                    <pre className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                      {typeof alternative.expectedOutcome === 'object'
                        ? JSON.stringify(alternative.expectedOutcome, null, 2)
                        : String(alternative.expectedOutcome)
                      }
                    </pre>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <div className="text-content-subtle text-4xl mb-2">🔀</div>
            <p className="text-content-secondary">No alternative options were considered</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Expected Impact Tab
const ExpectedImpactTab: React.FC<{ decision: AgentDecision }> = ({ decision }) => {
  return (
    <div className="space-y-6">
      <div>
        <h4 className="text-lg font-semibold text-content mb-3">Expected Impact</h4>
        <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-4">
          <p className="text-content">
            This decision is expected to have a <strong>{decision.riskLevel}</strong> risk impact
            with <strong>{(decision.confidence * 100).toFixed(1)}%</strong> confidence.
          </p>
        </div>
      </div>

      {decision.outcome && (
        <div>
          <h5 className="font-medium text-content mb-3">Actual Outcome</h5>
          <div className="border border-line rounded-lg p-4">
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="bg-gray-50 dark:bg-dark-700 rounded-lg p-3">
                <div className="text-sm text-content-secondary">Success</div>
                <div className="font-medium text-content">
                  {decision.outcome.success ? '✅ Yes' : '❌ No'}
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-dark-700 rounded-lg p-3">
                <div className="text-sm text-content-secondary">Variance from Expected</div>
                <div className="font-medium text-content">
                  {(decision.outcome.variance * 100).toFixed(1)}%
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <h6 className="font-medium text-content mb-2">Expected Result</h6>
                <div className="bg-gray-50 dark:bg-dark-700 rounded-lg p-3">
                  <pre className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                    {typeof decision.outcome.expectedResult === 'object'
                      ? JSON.stringify(decision.outcome.expectedResult, null, 2)
                      : String(decision.outcome.expectedResult)
                    }
                  </pre>
                </div>
              </div>

              <div>
                <h6 className="font-medium text-content mb-2">Actual Result</h6>
                <div className="bg-gray-50 dark:bg-dark-700 rounded-lg p-3">
                  <pre className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                    {typeof decision.outcome.actualResult === 'object'
                      ? JSON.stringify(decision.outcome.actualResult, null, 2)
                      : String(decision.outcome.actualResult)
                    }
                  </pre>
                </div>
              </div>

              {decision.outcome.learnings.length > 0 && (
                <div>
                  <h6 className="font-medium text-content mb-2">Learnings</h6>
                  <ul className="space-y-1">
                    {decision.outcome.learnings.map((learning, index) => (
                      <li key={index} className="text-sm text-gray-700 dark:text-gray-300 flex items-start space-x-2">
                        <span className="text-blue-600 dark:text-blue-300 mt-1">•</span>
                        <span>{learning}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};